import { useState } from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Card, Checkbox, Collapse, Divider, Input, Select, Space, Tag, Typography } from "antd";
import cloneDeep from "lodash.clonedeep";
import { useTranslation } from "react-i18next";

import { type PricingFeature, type PricingPlan } from "@fitness-app/data-models/entities/CheckoutVariant";

import PricingPlanPreview from "./PricingPlanPreview";

const { Panel } = Collapse;
const { Text, Title } = Typography;

interface ProductPagePricingPlansFormProps {
  plans: PricingPlan[];
  onChange: (plans: PricingPlan[]) => void;
}

const ProductPagePricingPlansForm = ({ plans, onChange }: ProductPagePricingPlansFormProps) => {
  const { t } = useTranslation(["products", "common"]);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const handleAddPlan = () => {
    if (plans.length < 2) {
      const newPlans = [
        ...plans,
        {
          title: "",
          regularPrice: "",
          promoPrice: "",
          monthlyInfo: "",
          features: [],
          bonuses: [],
        },
      ];
      onChange(newPlans);
    }
  };

  const handleRemovePlan = (index: number) => {
    const newPlans = plans.filter((_, i) => i !== index);
    onChange(newPlans);
  };

  const handleDuplicatePlan = (index: number) => {
    if (plans.length < 2) {
      const planToCopy = plans[index];
      if (!planToCopy) return;
      const newPlan = cloneDeep(planToCopy);
      newPlan.title = `${planToCopy.title} (Copy)`;
      const newPlans = [...plans, newPlan];
      onChange(newPlans);
    }
  };

  const handleCopyFeatures = (fromPlanIndex: number, toPlanIndex: number, type: "features" | "bonuses") => {
    const newPlans = cloneDeep(plans);
    const fromPlan = newPlans[fromPlanIndex];
    const toPlan = newPlans[toPlanIndex];
    if (!fromPlan || !toPlan) return;

    toPlan[type] = cloneDeep(fromPlan[type]);
    onChange(newPlans);
  };

  const handlePlanChange = (index: number, field: keyof PricingPlan, value: any) => {
    const newPlans = cloneDeep(plans);
    const plan = newPlans[index];
    if (!plan) return;
    // eslint-disable-next-line
    (plan[field] as any) = value;
    onChange(newPlans);
  };

  const handleAddFeature = (planIndex: number, type: "features" | "bonuses") => {
    const newPlans = cloneDeep(plans);
    const plan = newPlans[planIndex];
    if (!plan) return;
    plan[type].push({
      name: "",
      value: "",
      originalPrice: "",
      isFree: false,
      hideCheck: false,
      bolded: false,
    });
    onChange(newPlans);
  };

  const handleRemoveFeature = (planIndex: number, type: "features" | "bonuses", featureIndex: number) => {
    const newPlans = cloneDeep(plans);
    const plan = newPlans[planIndex];
    if (!plan) return;
    plan[type].splice(featureIndex, 1);
    onChange(newPlans);
  };

  const handleFeatureChange = (
    planIndex: number,
    type: "features" | "bonuses",
    featureIndex: number,
    field: keyof PricingFeature,
    value: any,
  ) => {
    const newPlans = cloneDeep(plans);
    const plan = newPlans[planIndex];
    if (!plan) return;
    const feature = plan[type][featureIndex];
    if (!feature) return;
    // eslint-disable-next-line
    (feature[field] as any) = value;
    onChange(newPlans);
  };

  const handleMoveFeature = (
    planIndex: number,
    type: "features" | "bonuses",
    featureIndex: number,
    direction: "up" | "down",
  ) => {
    const newPlans = cloneDeep(plans);
    const plan = newPlans[planIndex];
    if (!plan) return;
    const features = plan[type];
    const newIndex = direction === "up" ? featureIndex - 1 : featureIndex + 1;

    if (newIndex >= 0 && newIndex < features.length) {
      const currentFeature = features[featureIndex];
      const targetFeature = features[newIndex];
      if (!currentFeature || !targetFeature) return;
      features[featureIndex] = targetFeature;
      features[newIndex] = currentFeature;
      onChange(newPlans);
    }
  };

  const renderFeatureForm = (
    planIndex: number,
    type: "features" | "bonuses",
    feature: PricingFeature,
    featureIndex: number,
  ) => (
    <Card
      key={featureIndex}
      size="small"
      className="mb-2"
      extra={
        <Space>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            onClick={() => handleMoveFeature(planIndex, type, featureIndex, "up")}
            disabled={featureIndex === 0}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={() => handleMoveFeature(planIndex, type, featureIndex, "down")}
            disabled={featureIndex === (plans[planIndex]?.[type]?.length ?? 0) - 1}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveFeature(planIndex, type, featureIndex)}
          />
        </Space>
      }
    >
      <Space direction="vertical" className="w-full" size="small">
        <Input
          placeholder={t<string>("pageConfig.featureName")}
          value={feature.name}
          onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "name", e.target.value)}
        />
        <Input
          placeholder={t<string>("pageConfig.featureValue")}
          value={feature.value}
          onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "value", e.target.value)}
        />
        <Input
          placeholder={t<string>("pageConfig.featureOriginalPrice")}
          value={feature.originalPrice}
          onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "originalPrice", e.target.value)}
        />
        <Space direction="vertical" size="small" className="w-full">
          <Checkbox
            checked={feature.isFree}
            onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "isFree", e.target.checked)}
          >
            {t<string>("pageConfig.featureIsFree")}
          </Checkbox>
          <Checkbox
            checked={feature.hideCheck}
            onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "hideCheck", e.target.checked)}
          >
            {t<string>("pageConfig.featureHideCheck")}
          </Checkbox>
          <Checkbox
            checked={feature.bolded}
            onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "bolded", e.target.checked)}
          >
            {t<string>("pageConfig.featureBolded")}
          </Checkbox>
          <Checkbox
            checked={feature.striketrough}
            onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "striketrough", e.target.checked)}
          >
            {t<string>("pageConfig.featureStriketrough")}
          </Checkbox>
          <Checkbox
            checked={feature.unavailable}
            onChange={(e) => handleFeatureChange(planIndex, type, featureIndex, "unavailable", e.target.checked)}
          >
            Nie ma w tym pakiecie (✕, tylko tabela z opcjami płatności)
          </Checkbox>
        </Space>
      </Space>
    </Card>
  );

  const renderCopySelector = (planIndex: number, type: "features" | "bonuses") => {
    if (plans.length < 2) return null;

    const otherPlans = plans.map((p, idx) => ({ plan: p, index: idx })).filter(({ index }) => index !== planIndex);

    if (otherPlans.length === 0) return null;

    return (
      <Select
        placeholder={t<string>(`pageConfig.copyFrom${type === "features" ? "Features" : "Bonuses"}`)}
        style={{ width: 250 }}
        onChange={(fromIndex: number) => handleCopyFeatures(fromIndex, planIndex, type)}
        value={undefined}
      >
        {otherPlans.map(({ plan, index }) => (
          <Select.Option key={index} value={index}>
            {plan.title || `${t<string>("pageConfig.plan")} ${index + 1}`} ({plan[type].length})
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderPlanForm = (plan: PricingPlan, planIndex: number) => (
    <div key={planIndex} className="space-y-4">
      <Card
        title={
          <Space>
            <span>{t<string>("pageConfig.planDetails")}</span>
            {plan.best && <Tag color="green">{t<string>("pageConfig.bestPlan")}</Tag>}
          </Space>
        }
        extra={
          <Space>
            {plans.length < 2 && (
              <Button icon={<CopyOutlined />} onClick={() => handleDuplicatePlan(planIndex)}>
                {t<string>("common:button.duplicate")}
              </Button>
            )}
            {/*
             * Usuwanie działa też przy jednym planie. Wcześniej przycisk pojawiał
             * się dopiero od dwóch, przez co dodanego pojedynczo planu VIP nie dało
             * się skasować. Pusta lista jest legalna: zapis podmienia ją na
             * `undefined`, a sekcja cennika na stronie i tak sprawdza długość.
             */}
            <Button danger icon={<DeleteOutlined />} onClick={() => handleRemovePlan(planIndex)}>
              {t<string>("common:button.remove")}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" className="w-full" size="middle">
          <Input
            placeholder={t<string>("pageConfig.planTitle")}
            value={plan.title}
            onChange={(e) => handlePlanChange(planIndex, "title", e.target.value)}
            size="large"
          />
          <Input
            placeholder={t<string>("pageConfig.planSubtitle")}
            value={plan.subtitle}
            onChange={(e) => handlePlanChange(planIndex, "subtitle", e.target.value)}
          />
          <Space className="w-full" direction="horizontal">
            <Input
              placeholder={t<string>("pageConfig.regularPrice")}
              value={plan.regularPrice}
              onChange={(e) => handlePlanChange(planIndex, "regularPrice", e.target.value)}
              addonBefore={t<string>("pageConfig.regularPrice")}
            />
            <Input
              placeholder={t<string>("pageConfig.promoPrice")}
              value={plan.promoPrice}
              onChange={(e) => handlePlanChange(planIndex, "promoPrice", e.target.value)}
              addonBefore={t<string>("pageConfig.promoPrice")}
            />
          </Space>
          <Input
            placeholder={t<string>("pageConfig.monthlyInfo")}
            value={plan.monthlyInfo}
            onChange={(e) => handlePlanChange(planIndex, "monthlyInfo", e.target.value)}
          />
          <Input
            placeholder={t<string>("pageConfig.oneTimeInfo")}
            value={plan.oneTimeInfo}
            onChange={(e) => handlePlanChange(planIndex, "oneTimeInfo", e.target.value)}
          />
          <Input
            placeholder="Badge nad ceną, np. Najczęściej wybierany"
            value={plan.badge}
            onChange={(e) => handlePlanChange(planIndex, "badge", e.target.value)}
          />
          <Input
            placeholder="Napis na przycisku, np. Wybieram 12 miesięcy"
            value={plan.ctaLabel}
            onChange={(e) => handlePlanChange(planIndex, "ctaLabel", e.target.value)}
          />
          <Input
            placeholder="Cena zaznaczana przyciskiem w koszyku (price_...)"
            value={plan.priceId}
            onChange={(e) => handlePlanChange(planIndex, "priceId", e.target.value.trim() || undefined)}
          />
          <Checkbox checked={plan.best} onChange={(e) => handlePlanChange(planIndex, "best", e.target.checked)}>
            {t<string>("pageConfig.markAsBest")}
          </Checkbox>
        </Space>
      </Card>

      <Card title="Linie „albo …” pod ratą (tabela z opcjami płatności)">
        <Space direction="vertical" className="w-full">
          {(plan.paymentOptions ?? []).map((option, optionIndex) => (
            <Space key={optionIndex} className="w-full" align="start">
              <Input
                placeholder="albo 6 017 zł jednorazowo za 13 miesięcy"
                value={option.label}
                onChange={(e) =>
                  handlePlanChange(
                    planIndex,
                    "paymentOptions",
                    (plan.paymentOptions ?? []).map((item, index) =>
                      index === optionIndex ? { ...item, label: e.target.value } : item,
                    ),
                  )
                }
              />
              <Input
                placeholder="oszczędzasz 1 094 zł"
                value={option.savings}
                onChange={(e) =>
                  handlePlanChange(
                    planIndex,
                    "paymentOptions",
                    (plan.paymentOptions ?? []).map((item, index) =>
                      index === optionIndex ? { ...item, savings: e.target.value } : item,
                    ),
                  )
                }
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  handlePlanChange(
                    planIndex,
                    "paymentOptions",
                    (plan.paymentOptions ?? []).filter((_, index) => index !== optionIndex),
                  )
                }
              />
            </Space>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            block
            onClick={() =>
              handlePlanChange(planIndex, "paymentOptions", [
                ...(plan.paymentOptions ?? []),
                { label: "", savings: "" },
              ])
            }
          >
            Dodaj linię
          </Button>
        </Space>
      </Card>

      <Card title={t<string>("pageConfig.features")} extra={renderCopySelector(planIndex, "features")}>
        <Space direction="vertical" className="w-full">
          {plan.features.map((feature, featureIndex) =>
            renderFeatureForm(planIndex, "features", feature, featureIndex),
          )}
          <Button type="dashed" onClick={() => handleAddFeature(planIndex, "features")} icon={<PlusOutlined />} block>
            {t<string>("pageConfig.addFeature")}
          </Button>
        </Space>
      </Card>

      <Card title={t<string>("pageConfig.bonuses")} extra={renderCopySelector(planIndex, "bonuses")}>
        <Space direction="vertical" className="w-full">
          {plan.bonuses.map((bonus, bonusIndex) => renderFeatureForm(planIndex, "bonuses", bonus, bonusIndex))}
          <Button type="dashed" onClick={() => handleAddFeature(planIndex, "bonuses")} icon={<PlusOutlined />} block>
            {t<string>("pageConfig.addBonus")}
          </Button>
        </Space>
      </Card>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <Space direction="vertical" className="w-full" size="large">
          <div className="flex items-center justify-between">
            <Title level={4}>{t<string>("pageConfig.pricingPlans")}</Title>
            <Space>
              <Button icon={<EyeOutlined />} onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? t<string>("pageConfig.hidePreview") : t<string>("pageConfig.showPreview")}
              </Button>
              {plans.length < 2 && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPlan}>
                  {t<string>("pageConfig.addPlan")}
                </Button>
              )}
            </Space>
          </div>

          {plans.length === 0 && <Text type="secondary">{t<string>("pageConfig.noPricingPlans")}</Text>}

          {showPreview && plans.length > 0 && (
            <>
              <PricingPlanPreview plans={plans} />
              <Divider />
            </>
          )}

          {plans.length > 0 && (
            <Collapse defaultActiveKey={plans.map((_, index) => index.toString())}>
              {plans.map((plan, index) => (
                <Panel
                  header={
                    <Space>
                      <span>{plan.title || `${t<string>("pageConfig.plan")} ${index + 1}`}</span>
                      {plan.best && <Tag color="green">{t<string>("pageConfig.best")}</Tag>}
                    </Space>
                  }
                  key={index.toString()}
                >
                  {renderPlanForm(plan, index)}
                </Panel>
              ))}
            </Collapse>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default ProductPagePricingPlansForm;
